import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'

import { TCODEVIEW, ENCOUNTERVIEW } from 'Constants'
import { setTcodeViewType } from 'Store/encounters'

import EncountersService from 'Services/encounters'
import EventService from 'Services/events'
import config from 'Config'

import { Flex, Grid } from 'lese'

import { H1, LabelWithTitle } from 'Components/shared/Typography'
import { Tabs, TabList, Tab, TabContent } from 'Components/shared/Tabs'
import { ClickableLink } from 'Components/shared/Link'
import { convertToReadableDate } from 'Helpers'
import { toastWithErrorMessage } from 'Helpers/toast'
import Header from './Header'
import Comments from './Comments/index'
import Events from './Events/index'
import CommentForm from './Comments/CommentForm'
import Tcodes from './Tcodes'

const ViewerContentContainer = styled(Flex)`
  margin: 32px 1px 32px 32px;
  height: 100%;
`
const ViewerObject = styled.iframe`
  object-fit: contain;
`

const JourneyInfoRow = styled(Grid)`
  margin-top: 8px;
`

export default ({ selectedHl7Id, viewType }) => {
  const [encounter, setEncounter] = useState({})
  const [isExternal, setExternal] = useState(false)

  async function claimEncounter(enc) {
    // Check to make sure it is still in the un-reviewed status and unassigned
    if (enc.review.status !== 0 || enc.review.userId !== null) return

    // Get the updated encounter
    const updatedEncounter = await EncountersService.claim({
      encounterId: enc.id
    }).catch(toastWithErrorMessage('Failed to claim encounter'))

    // If we did not get one, exit. There was some error
    if (!updatedEncounter) return

    // Create an event and mark it as in review for the encounter
    await EventService.create({
      type: 'encounterEvent',
      event: 'update',
      encounterId: updatedEncounter.id,
      data: {
        previousStatus: 0,
        newStatus: updatedEncounter.review.status
      }
    }).catch(toastWithErrorMessage('Failed to claim encounter'))

    // Create an event and mark the owner for the encounter
    await EventService.create({
      type: 'encounterEvent',
      event: 'updateOwner',
      encounterId: updatedEncounter.id,
      data: {
        previousOwner: null,
        newOwner: updatedEncounter.review.userId
      }
    }).catch(toastWithErrorMessage('Failed to claim encounter'))

    // Update the state for the encounter with the in review version
    setEncounter(updatedEncounter)
  }

  useEffect(async () => {
    // Check to make sure we got an encounter to search for
    if (!selectedHl7Id) return
    // TODO: Handle error -> Toast message?

    // Get an encounter based off of the hl7Id
    const fetchedEncounter = await EncountersService.get({ hl7Id: selectedHl7Id })

    // Set the encounter with the one from the database
    setEncounter(fetchedEncounter)

    // Claim the encounter for who ever is viewing it
    claimEncounter(fetchedEncounter)

    // Get if its an external encounter
    setExternal(fetchedEncounter.isExternal)

    // Handle resize of viewer iFrame
  }, [selectedHl7Id])

  // Get the encounter and update the state so we get the updated version with correct comments and events
  async function updateRemarks() {
    const fetchedEncounter = await EncountersService.get({ hl7Id: selectedHl7Id })
    setEncounter(fetchedEncounter)
  }

  const title = (
    <ClickableLink onClick={setTcodeViewType}>
      <H1 title="Click to see patient's T-Code list">Pt#: {encounter.medicalRef}</H1>
    </ClickableLink>
  )

  return (
    <>
      {viewType === ENCOUNTERVIEW && (
        <>
          <Header encounter={encounter} title={title} updateRemarks={updateRemarks} />
          <Flex column>
            <Tabs>
              <TabList>
                <Tab>JOURNEY INFO</Tab>
                <Tab>COMMENTS</Tab>
                <Tab>EVENTS</Tab>
              </TabList>
              <TabContent>
                {/* Journey Info */}
                <Grid columns="50% 1fr 1fr" rows="1fr" gap="8px">
                  <LabelWithTitle title="Sent-in On">
                    {convertToReadableDate(encounter.sentDate)}
                  </LabelWithTitle>
                  <LabelWithTitle title="Pt. Initials">
                    {encounter.patient?.initials}
                  </LabelWithTitle>
                  <LabelWithTitle title="Pt. Birth Year">
                    {encounter.patient?.yearOfBirth}
                  </LabelWithTitle>
                </Grid>
                <JourneyInfoRow columns="50% 1fr 1fr" rows="1fr" gap="8px">
                  <LabelWithTitle title="Journey">{encounter.journey?.title}</LabelWithTitle>
                  <LabelWithTitle title="Pt. Gender">{encounter.patient?.gender}</LabelWithTitle>
                  <LabelWithTitle title="Provider Code">
                    {encounter.patient?.doctorCode}
                  </LabelWithTitle>
                </JourneyInfoRow>
                <JourneyInfoRow columns="50% 1fr 1fr" rows="1fr" gap="8px">
                  <LabelWithTitle title="Started">
                    {convertToReadableDate(encounter.journey?.startDate)}
                  </LabelWithTitle>
                  <LabelWithTitle title="PMHID#">{encounter.PMHID}</LabelWithTitle>
                  <LabelWithTitle title="Group">{encounter.group}</LabelWithTitle>
                </JourneyInfoRow>
              </TabContent>
              {/* Comments */}
              <TabContent>
                <div>
                  <CommentForm selectedEncounter={encounter} updateComments={updateRemarks} />
                  <Comments encounter={encounter} />
                </div>
              </TabContent>
              {/* Events */}
              <TabContent>
                <div>
                  <Events encounter={encounter} />
                </div>
              </TabContent>
            </Tabs>
            <ViewerContentContainer>
              {isExternal && encounter.PMHID && (
                <ViewerObject
                  src={`${config.docportal.getUrl()}/docportal_api/services/get_ts_file.php?PMHID=${
                    encounter.PMHID
                  }&token=${encounter.token}`}
                  key={encounter.PMHID}
                  width="100%"
                  height="100%"
                >
                  PDF Document {/* TODO: Better aria-label */}
                </ViewerObject>
              )}
              {!isExternal && encounter.PMHID && (
                <ViewerObject
                  src={`${config.docportal.getUrl()}/docportal/#/getFaxReport/${
                    encounter.PMHID
                  }?token=${encounter.token}`}
                  key={encounter.PMHID}
                  width="100%"
                  height="100%"
                >
                  PDF Document {/* TODO: Better aria-label */}
                </ViewerObject>
              )}
            </ViewerContentContainer>
          </Flex>
        </>
      )}
      {viewType === TCODEVIEW && <Tcodes medicalRef={encounter.medicalRef} title={title} />}
    </>
  )
}
